<template>
	<GlobalHomeNew v-if="isNewStartPage" @scrollToTop="$emit('scrollToTop')">
		<template #sponsoredRecommendation>
			<GlobalHomeSrWrapper v-if="canDisplaySR" :isListCreative="isListSponsoredAd">
				<SponsoredRecommendation
					:key="sponsoredAd?.bidId"
					:sponsoredAd="sponsoredAd"
					:fullWidth="true"
					:lazy="false"
					placement="sr_home_global"
					:sraPlacement="SraPlacement.HomeGlobal"
				/>
			</GlobalHomeSrWrapper>

			<SponsoredRecommendation
				v-else-if="isShadowCampaign"
				:key="sponsoredAd?.bidId"
				:sponsoredAd="sponsoredAd"
				:fullWidth="true"
				:lazy="false"
				placement="sr_home_global"
				:sraPlacement="SraPlacement.HomeGlobal"
			/>
		</template>
	</GlobalHomeNew>

	<GlobalHomeLegacy v-else @scrollToTop="$emit('scrollToTop')">
		<template #sponsoredRecommendation>
			<!-- Shadow impressions only: legacy countries have no SR designs, so no SR shown. -->
			<SponsoredRecommendation
				v-if="isShadowCampaign"
				:key="sponsoredAd?.bidId"
				:sponsoredAd="sponsoredAd"
				:fullWidth="true"
				:lazy="false"
				placement="sr_home_global"
				:sraPlacement="SraPlacement.HomeGlobal"
			/>
		</template>
	</GlobalHomeLegacy>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

import { AdPlacement, SraPlacement } from '@/@types/graphql-types';
import GlobalHomeSrWrapper from '@/features/sponsored-recommendations/components/GlobalHomeSrWrapper.vue';
import SponsoredRecommendation from '@/features/sponsored-recommendations/components/SponsoredRecommendation.vue';
import { useSendBidId } from '@/features/sponsored-recommendations/composables/useSendBidId';
import { useGetSrPlacement } from '@/features/sponsored-recommendations/composables/useGetSrPlacement';
import adminConfig from '@/features/promotions/config.json';

const GlobalHomeNew = defineAsyncComponent(() => import('@/features/global-home/components/GlobalHomeNew.vue'));
const GlobalHomeLegacy = defineAsyncComponent(() => import('@/features/global-home/components/GlobalHomeLegacy.vue'));

const NEW_START_PAGE_COUNTRIES = ['DE', 'AU', 'GB', 'NZ', 'US'];

const { country } = storeToRefs(useLanguageStore());

const isNewStartPage = computed(() => NEW_START_PAGE_COUNTRIES.includes(country.value));

// ** SRs Adserver Migration ** //
const homeGlobalSrConfig = adminConfig?.ad_server?.srs?.placements?.home_global_sr;
const useAdserverSrMutation = computed(
	() => homeGlobalSrConfig?.all_countries || homeGlobalSrConfig?.countries?.includes(country.value)
);

// Be mindful that GlobalHomeLegacy has no active SRs or SR designs
// For SRs can only be run in `NEW_START_PAGE_COUNTRIES`
const { sponsoredAd, hasActiveSRCampaign, isListSponsoredAd, isShadowCampaign } = useAdserverSrMutation.value
	? useGetSrPlacement({
			pageType: 'VIEW_HOME_GLOBAL',
			placement: SraPlacement.HomeGlobal,
			adPlacement: AdPlacement.HomeGlobalSr,
	  })
	: useSendBidId({
			pageType: 'VIEW_HOME_GLOBAL',
			placement: SraPlacement.HomeGlobal,
	  });

/** SR designs only exist on the new start page, this is a guard for campaigns mistakenly targeting non-new start page countries */
const canDisplaySR = computed(() => isNewStartPage.value && hasActiveSRCampaign.value);
</script>
